import { useLocation, useParams } from "@tanstack/react-router";
import { useMemo } from "react";

import type { ComboboxLabelValue } from "@/components/ui/combobox";
import { useAuth } from "@/features/auth";
import { useWorkspacesQuery } from "@/features/containers";
import {
  ContainerCombobox,
  renderContainerComboboxItemWithOptionalSeparator,
} from "@/features/containers/components/container-combobox";
import { useGoToWorkspace } from "@/features/workspaces/workspaces.hooks";

interface WorkspaceComboboxProps {
  /**
   * CR-144: explicit workspace for pages without a $slug route param
   * (Settings). Omitted, the combobox reads the route params as before.
   */
  workspaceSlug?: string;
  /**
   * CR-226: label for the current workspace while the member list does not
   * (yet) contain it — the header passes the route-resolved name. The list
   * item wins once it is there; omitted, the slug is shown instead.
   */
  workspaceName?: string;
}

export const WorkspaceCombobox = ({
  workspaceSlug,
  workspaceName,
}: WorkspaceComboboxProps = {}) => {
  const { goToWorkspace } = useGoToWorkspace();
  const { user } = useAuth();

  const params = useParams({ strict: false });
  const slug = workspaceSlug ?? params.slug;
  const { pathname } = useLocation();
  const isAllWorkspacesPage = pathname === "/workspaces";

  const { data: workspaces, isLoading } = useWorkspacesQuery(user.id);

  const workspacesForCombobox: ComboboxLabelValue[] = useMemo(
    () => [
      ...(!isAllWorkspacesPage
        ? ([
            {
              label: "All workspaces",
              value: "/",
            },
          ] as const)
        : []),
      ...(workspaces?.map((workspace) => ({
        label: workspace.name || workspace.slug,
        value: workspace.slug,
      })) || []),
    ],
    [isAllWorkspacesPage, workspaces],
  );

  // CR-226: the fallback is selection-only — not added to the items, so the
  // dropdown stays member-scoped and never shows the workspace twice. Memoized
  // so a header re-render does not reset ContainerCombobox's typed search.
  const selectedWorkspace = useMemo(
    () =>
      !slug
        ? null
        : (workspacesForCombobox.find(({ value }) => value === slug) ?? {
            label: workspaceName || slug,
            value: slug,
          }),
    [slug, workspaceName, workspacesForCombobox],
  );

  return (
    <ContainerCombobox<ComboboxLabelValue>
      items={workspacesForCombobox}
      selectedValue={selectedWorkspace}
      isLoading={isLoading}
      placeholder="Select a workspace"
      emptyText="No workspaces found"
      shrinkToFit={!selectedWorkspace ? 150 : Math.min(selectedWorkspace.label.length * 15, 150)} // TODO: solve with css
      onSelect={(next) => {
        if (next.value) {
          goToWorkspace(next.value);
        }
      }}
      renderItem={(item) =>
        item.value === "/"
          ? renderContainerComboboxItemWithOptionalSeparator({
              item,
              shouldRenderSeparatorAfter: true,
            })
          : renderContainerComboboxItemWithOptionalSeparator({
              item,
              shouldRenderSeparatorAfter: false,
            })
      }
    />
  );
};
